const FINANCIAL_PLANNING_BASE = '/v3/api/aawz-financial-planning'
const OPEN_FINANCE_BASE = '/v3/api/aawz-open-finance'

export const fiscal = {
  impacto: `${FINANCIAL_PLANNING_BASE}/impacto-fiscal`,
}

/**
 * Superfície de rede do CLIENTE final logado — nove leituras e exatamente uma
 * escrita (troca do par categoria + subcategoria de uma transação própria).
 *
 * ★ NENHUMA destas URLs carrega identificador de cliente. A identidade vem da
 * sessão (`resolve_client_context`), e é isso que faz o gate anti-IDOR do
 * backend funcionar. Acrescentar um id ao path aqui já quebra o contrato.
 *
 * ★ NÃO existe `goals`. O endpoint `GET /client/goals` foi cortado no contrato
 * por não ter consumidor — quem alimenta o slice `goals` é `goalsProgress`.
 * Acrescentá-lo é ressuscitar rota que o backend não implementa.
 */
export const client = {
  transactions: `${FINANCIAL_PLANNING_BASE}/client/transactions`,
  transactionCategory: (transactionId) =>
    `${FINANCIAL_PLANNING_BASE}/client/transactions/${transactionId}/category`,
  goalsProgress: `${FINANCIAL_PLANNING_BASE}/client/goals/progress`,
  categories: `${FINANCIAL_PLANNING_BASE}/client/categories`,
  subcategories: `${FINANCIAL_PLANNING_BASE}/client/subcategories`,
  budgetOverview: `${FINANCIAL_PLANNING_BASE}/client/budget-overview`,
  patrimonyConsolidated: `${FINANCIAL_PLANNING_BASE}/client/patrimony-consolidated`,
  patrimonyEvolution: `${FINANCIAL_PLANNING_BASE}/client/patrimony-evolution`,
  mainSimulation: `${FINANCIAL_PLANNING_BASE}/client/main-simulation`,
  dataFreshness: `${FINANCIAL_PLANNING_BASE}/client/data-freshness`,
}

export const transactions = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/transactions`,
  detail: (clientId, transactionId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/transactions/${transactionId}`,
  batch: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/transactions-batch`,
}

export const budgets = {
  list: (clientId) => `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/budgets`,
  detail: (budgetId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/budgets/${budgetId}`,
  // Endpoint legado de import/export por arquivo/tabela.
  batch: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/budgets-batch`,
  // Endpoint dedicado para batch upsert (POST /clients/<id>/orcamento/batch-upsert).
  batchUpsert: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/orcamento/batch-upsert`,
  patrimonyEvolution: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony-evolution`,
}

export const patrimony = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony`,
  detail: (clientId, patrimonyId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony/${patrimonyId}`,
}

export const patrimonyGoals = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony-goals`,
  detail: (clientId, goalLinkId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony-goals/${goalLinkId}`,
}

export const assetsGoals = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/assets-goals`,
  detail: (clientId, goalLinkId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/assets-goals/${goalLinkId}`,
}

export const goals = {
  list: (clientId) => `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/goals`,
  detail: (clientId, goalId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/goals/${goalId}`,
  batch: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/goals-batch`,
}
export const receitasOuDespesas = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/receitas-e-despesas-esperadas`,
  detail: (clientId, id) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/receitas-e-despesas-esperadas/${id}`,
  batch: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/receitas-e-despesas-esperadas-batch`,
}

export const simulacoes = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/simulacoes`,
  detail: (clientId, simulationId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/simulacoes/${simulationId}`,
  monteCarlo: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/simulations/monte-carlo`,
  monteCarloDetail: (clientId, simulationId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/simulations/monte-carlo/${simulationId}`,
  // Roda a simulação no motor do BACKEND sem persistir (AAWZ-10) — fonte de
  // verdade para probabilidadeSucesso, que o motor client-side não reproduz
  // (RNG do JS diverge do numpy).
  monteCarloPreview: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/simulations/monte-carlo/preview`,

  getMonteCarlo: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony-evolution/monte-carlo`,
  getMonteCarloDetail: (clientId, simulationId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/patrimony-evolution/monte-carlo/${simulationId}`,
}

export const categories = {
  list: () => `${FINANCIAL_PLANNING_BASE}/categories`,
  detail: (categoryId) => `${FINANCIAL_PLANNING_BASE}/categories/${categoryId}`,
  // Endpoint dedicado para batch delete (DELETE /categories/batch).
  batchDelete: () => `${FINANCIAL_PLANNING_BASE}/categories/batch`,
  // Endpoint dedicado para duplicar (POST /categories/<uuid>/duplicate).
  duplicate: (categoryId) =>
    `${FINANCIAL_PLANNING_BASE}/categories/${categoryId}/duplicate`,
  // Seed dos templates padrão (POST). Frontend chama no onboarding do
  // escritório — substitui o antigo seed-on-read no GET /categories.
  defaultTemplates: () =>
    `${FINANCIAL_PLANNING_BASE}/categories/default-templates`,
  templates: () => `${FINANCIAL_PLANNING_BASE}/categories/templates`,
  templateApply: (templateId) =>
    `${FINANCIAL_PLANNING_BASE}/categories/templates/${templateId}/apply`,
  templateDetail: (templateId) =>
    `${FINANCIAL_PLANNING_BASE}/categories/templates/${templateId}`,
}

export const subcategories = {
  list: () => `${FINANCIAL_PLANNING_BASE}/subcategories`,
  detail: (subId) => `${FINANCIAL_PLANNING_BASE}/subcategories/${subId}`,
  batch: () => `${FINANCIAL_PLANNING_BASE}/subcategories-batch`,
}

export const categorizationRules = {
  list: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/categorization-rules`,
  detail: (clientId, ruleId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/categorization-rules/${ruleId}`,
  batch: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/categorization-rules-batch`,
}
export const openFinance = {
  connectors: () => `${OPEN_FINANCE_BASE}/connectors`,
  clientConnectors: (clientId) => `${OPEN_FINANCE_BASE}/${clientId}/connectors`,
  connectorSynchronization: (clientId, connectorId) =>
    `${OPEN_FINANCE_BASE}/${clientId}/synchronizations/${connectorId}`,
  /**
   * @method GET Conexões Open Finance de TODOS os clientes do escritório —
   * visão de adoção do gestor. O escritório sai da sessão no backend; não há
   * (nem pode haver) parâmetro de escritório aqui.
   * @param {String}  [status_bucket] conectada | erro | aguardando | atualizando | desconhecido
   * @param {String}  [instituicao]
   * @param {String}  [busca] nome do cliente, código ou documento
   * @param {Boolean} [sem_sync] apenas as que nunca sincronizaram
   * @param {String}  [sort_by] cliente | status | saldo_atual |
   *                            data_ultima_sincronizacao | data_registro
   * @param {Boolean} [ascending]
   * @param {Integer} [limit] @param {Integer} [offset]
   */
  officeConnections: () => `${OPEN_FINANCE_BASE}/office/connections`,
  /**
   * @method GET Exporta a visão de adoção em xlsx (mesmos filtros, sem paginar).
   */
  officeConnectionsExport: () =>
    `${OPEN_FINANCE_BASE}/office/connections/export`,
}
export const pgbl = {
  impacto: '/v3/api/aawz-financial-planning/impacto-fiscal',
  manage: '/v3/api/aawz-financial-planning/pgbl-simulation',
  simulationApp: '/v3/api/aawz-financial-planning/pgbl-simulation',
  simulationPartners:
    '/v3/api/aawz-financial-planning/pgbl-simulation/from-partners',
  /**
   * @method GET buscar clientes (Dados mais leves usado no Financial Planning - PGBL)
   */
  customers: `/v2/api/aawz-partners/clients`,
}

export const report = {
  generate: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/report-pdf`,
  status: (clientId, hash) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/report-pdf/${hash}/status`,
  download: (clientId, hash) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/report-pdf/${hash}/download`,
  previewData: (clientId) =>
    `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/report-pdf/preview-data`,
}

export const officeReportConfig = {
  base: `${FINANCIAL_PLANNING_BASE}/office/report-config`,
}

export const clientReportConfig = {
  base: (clientId) => `${FINANCIAL_PLANNING_BASE}/clients/${clientId}/report-config`,
}
